import { createSelector } from '@reduxjs/toolkit';
import { actAdapter } from '@client/slices/entities/acts.reducer';
import { affiliateAdapter } from '@client/slices/entities/affiliates.reducer';
import { beaconAdapter } from '@client/slices/entities/beacons.reducer';
import { categoryAdapter } from '@client/slices/entities/categories.reducer';
import { documentAdapter } from '@client/slices/entities/documents.reducer';
import { hyperwalletAccountAdapter } from '@client/slices/entities/hyperwallet-accounts.reducer';
import { hyperwalletLogAdapter } from '@client/slices/entities/hyperwallet-logs.reducer';
import { invitationAdapter } from '@client/slices/entities/invitations.reducer';
import { linkAdapter } from '@client/slices/entities/links.reducer';
import { notificationAdapter } from '@client/slices/entities/notifications.reducer';
import { payoutAdapter } from '@client/slices/entities/payouts.reducer';
import { productAdapter } from '@client/slices/entities/products.reducer';
import { requestAdapter } from '@client/slices/entities/requests.reducer';
import { reviewAdapter } from '@client/slices/entities/reviews.reducer';
import { showAdapter } from '@client/slices/entities/shows.reducer';
import { threadAdapter } from '@client/slices/entities/threads.reducer';
import { ticketAdapter } from '@client/slices/entities/tickets.reducer';
import { typingStateAdapter } from '@client/slices/entities/typing-states.reducer';
import { userAdapter } from '@client/slices/entities/users.reducer';
import { venueAdapter } from '@client/slices/entities/venues.reducer';
import { homeAdapter } from '@client/slices/features/home.reducer';
import { mapAdapter } from '@client/slices/features/map.reducer';
import { messageAdapter } from '@client/slices/entities/messages.reducer';
import { profileAdapter } from '@client/slices/entities/profiles.reducer';
import type { RootState } from '../store';

const selector = (state: RootState) => state;

// helpers
export const listenerSelector = createSelector(selector, (state) => state.listeners.items);
export const loaderSelector = createSelector(selector, (state) => state.loaders.scopes);

// Notifications
export const notificationSelectors = notificationAdapter.getSelectors<RootState>((state) => state.notifications);
export const unseenNotificationSelector = createSelector(selector, (state) => state.notifications.unseenItems);

// Tickets
export const ticketSelectors = ticketAdapter.getSelectors<RootState>((state) => state.tickets);

// acts

// shows
export const showSelectors = showAdapter.getSelectors<RootState>((state) => state.shows);
export const currentShowIdSelector = createSelector(selector, (state) => state.shows.current);
export const showMetadataSelector = createSelector(selector, (state) => state.shows.metadata);

// maps
export const mapSelectors = mapAdapter.getSelectors<RootState>((state) => state.map);
export const mapBoundsSelector = createSelector(selector, (state) => state.map.bounds);
export const mapExtraHitsSelector = createSelector(selector, (state) => state.map.extraHits);
export const mapHoverIdSelector = createSelector(selector, (state) => state.map.hoverId);
export const mapObjectSelector = createSelector(selector, (state) => state.map.map);
export const mapZoomSelector = createSelector(selector, (state) => state.map.zoom);

// settlements
export const settlementCurrentSelector = createSelector(selector, (state) => state.settlement.current);
export const settlementOrderItemsSelector = createSelector(selector, (state) => state.settlement.orderItems);
export const settlementSkuSelector = createSelector(selector, (state) => state.settlement.sku);
export const settlementIsLoadedSelector = createSelector(selector, (state) => state.settlement.isLoaded);
export const settlementIsLoadingSelector = createSelector(selector, (state) => state.settlement.isLoading);
export const settlementMetadataSelector = createSelector(selector, (state) => state.settlement.metadata);
export const settlementPayoutsByCountrySelector = createSelector(selector, (state) => state.settlement.countryMetadata);
export const settlementPayoutsByProductSelector = createSelector(selector, (state) => state.settlement.productMetadata);

// threads
export const threadSelectors = threadAdapter.getSelectors<RootState>((state) => state.threads);
export const currentThreadSelector = createSelector(selector, (state) => state.threads.current);
export const threadDeletionSelector = createSelector(selector, (state) => state.threads.threadDeletions);

// session
export const sessionUserSelector = createSelector(selector, (state) => state.session.user);
export const anonymousUserSelector = createSelector(selector, (state) => state.session.anonymousUser);
export const locationSelector = createSelector(selector, (state) => state.router.location.pathname);
export const fingerprintSelector = createSelector(selector, (state) => state.session.fingerprint);
export const logoutPendingSelector = createSelector(selector, (state) => state.session.logoutPending);
export const impersonationPendingSelector = createSelector(selector, (state) => state.session.impersonationPending);
export const defaultZoneNameSelector = createSelector(selector, (state) => state.session.defaultZoneName);
export const sessionPositionSelector = createSelector(selector, (state) => state.session.position);
export const sessionUserCookiesState = createSelector(selector, (state) => state.session.acceptsCookies);
export const sessionGoogleClientIdSelector = createSelector(selector, (state) => state.session.googleAnalyticsClientId);

// clients
export const firebaseSelector = createSelector(selector, (state) => state.clients.firebase);
export const firebaseLoggedInSelector = createSelector(selector, (state) => state.clients.firebaseLoggedIn);
export const firebaseScaffoldedSelector = createSelector(selector, (state) => state.clients.firebaseScaffolded);

// stripe
export const stripeCurrentSKUSelector = createSelector(selector, (state) => state.stripe.currentSKU);
export const stripeCurrentSKUAmountSelector = createSelector(selector, (state) => state.stripe.currentSKUAmount);
export const stripeCurrentOrderSelector = createSelector(selector, (state) => state.stripe.currentOrder);
export const stripeIdempotencyKeySelector = createSelector(selector, (state) => state.stripe.idempotencyKey);

// Messages
export const messageSelectors = messageAdapter.getSelectors<RootState>((state) => state.messages);
export const messageChatOptionsSelector = createSelector(selector, (state) => state.messages.chatOptions);
export const messageHasNewSelector = createSelector(selector, (state) => state.messages.hasNew);
export const messageHasMoreSelector = createSelector(selector, (state) => state.messages.hasMore);

// Hyperwallet
export const hyperwalletAccountSelectors = hyperwalletAccountAdapter.getSelectors<RootState>((state) => state.hyperwalletAccounts);
export const hyperwalletLogSelectors = hyperwalletLogAdapter.getSelectors<RootState>((state) => state.hyperwalletLogs);
export const hyperwalletDestinationAccountSelector = createSelector(selector, (state) => state.hyperwalletAccounts.destinationAccount);

// Users
export const userSelectors = userAdapter.getSelectors<RootState>((state) => state.users);
export const userCurrentSelector = createSelector(selector, (state) => state.users.current);

// Entity Adapter Selectors
export const actSelectors = actAdapter.getSelectors<RootState>((state) => state.acts);
export const affiliateSelectors = affiliateAdapter.getSelectors<RootState>((state) => state.affiliates);
export const beaconSelectors = beaconAdapter.getSelectors<RootState>((state) => state.beacons);
export const categorySelectors = categoryAdapter.getSelectors<RootState>((state) => state.categories);
export const documentSelectors = documentAdapter.getSelectors<RootState>((state) => state.documents);
export const homeSelectors = homeAdapter.getSelectors<RootState>((state) => state.home);
export const invitationSelectors = invitationAdapter.getSelectors<RootState>((state) => state.invitations);
export const linkSelectors = linkAdapter.getSelectors<RootState>((state) => state.links);
export const payoutSelectors = payoutAdapter.getSelectors<RootState>((state) => state.payouts);
export const productSelectors = productAdapter.getSelectors<RootState>((state) => state.products);
export const profileSelectors = profileAdapter.getSelectors<RootState>((state) => state.profiles);
export const requestSelectors = requestAdapter.getSelectors<RootState>((state) => state.requests);
export const reviewSelectors = reviewAdapter.getSelectors<RootState>((state) => state.reviews);
export const typingStateSelectors = typingStateAdapter.getSelectors<RootState>((state) => state.typingStates);
export const venueSelectors = venueAdapter.getSelectors<RootState>((state) => state.venues);
